import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../api";
import { useCompany } from "../Company/CompanyContext";
import { toast } from "react-toastify";

export default function CompanyTab({ raw, onSaved }) {
  const { t } = useTranslation();
  const { reload } = useCompany();
  const [form, setForm] = useState({
    company_name:    raw.company_name    || "",
    company_slogan:  raw.company_slogan  || "",
    company_email:   raw.company_email   || "",
    company_phone:   raw.company_phone   || "",
    company_address: raw.company_address || "",
    company_city:    raw.company_city    || "",
    company_zip:     raw.company_zip     || "",
    company_siret:   raw.company_siret   || "",
    company_iban:    raw.company_iban    || "",
  });
  const [logoFile, setLogoFile]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [uploading, setUploading] = useState(false);

  const handle = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      await reload();
      toast.success(t("settings.company.saved"));
      onSaved?.();
    } catch {
      toast.error(t("settings.common.saveError"));
    } finally { setSaving(false); }
  };

  const uploadLogo = async () => {
    if (!logoFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("logo", logoFile);
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await reload();
      toast.success(t("settings.company.logoUpdated"));
      setLogoFile(null);
    } catch {
      toast.error(t("settings.company.logoError"));
    } finally { setUploading(false); }
  };

  const Field = ({ label, name, type = "text", placeholder = "" }) => (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-semibold text-base-content/70">{label}</label>
      <input
        type={type}
        name={name}
        value={form[name]}
        onChange={handle}
        placeholder={placeholder}
        className="input input-bordered bg-base-200 border-base-300 focus:border-primary focus:outline-none"
      />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label={t("settings.company.name")}    name="company_name"    placeholder="Mon Entreprise" />
        <Field label={t("settings.company.slogan")}  name="company_slogan"  placeholder="Osez bouger" />
        <Field label={t("settings.company.email")}   name="company_email"   type="email" placeholder="contact@entreprise.fr" />
        <Field label={t("settings.company.phone")}   name="company_phone"   placeholder="01 23 45 67 89" />
        <Field label={t("settings.company.siret")}   name="company_siret"   placeholder="123 456 789 00000" />
        <Field label={t("settings.company.address")} name="company_address" placeholder="1 rue de la Paix" />
        <div className="grid grid-cols-2 gap-2">
          <Field label={t("settings.company.zip")}  name="company_zip"  placeholder="75001" />
          <Field label={t("settings.company.city")} name="company_city" placeholder="Paris" />
        </div>
        <div className="md:col-span-2">
          <Field label={t("settings.company.iban")} name="company_iban" placeholder="FR76 1234 5678..." />
        </div>
      </div>

      {/* Logo upload */}
      <div className="border border-base-300 rounded-xl p-4 space-y-3">
        <h4 className="font-semibold text-sm text-base-content/70">{t("settings.company.logoTitle")}</h4>
        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setLogoFile(e.target.files[0] || null)}
            className="file-input file-input-bordered file-input-sm flex-1 min-w-0"
          />
          <button
            onClick={uploadLogo}
            disabled={!logoFile || uploading}
            className="btn btn-sm btn-primary"
          >
            {uploading ? t("settings.company.uploading") : t("settings.company.upload")}
          </button>
        </div>
        <p className="text-xs text-base-content/40">{t("settings.company.logoHint")}</p>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="btn btn-primary">
          {saving ? t("settings.common.saving") : t("settings.common.save")}
        </button>
      </div>
    </div>
  );
}
